import React from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { useAddUserToDevice } from "hooks/useDevices";
import { useQueryClient } from "@tanstack/react-query";
import { notify } from "utils/notify";
import { FormField } from "components/shared/FormField";
import { FormLabel } from "components/shared/FormLabel";
import { FormError, getFieldAriaProps, fieldErrorId } from "components/shared/FormError";

const schema = yup.object({
  email: yup
    .string()
    .required("Email is required")
    .email("Please enter a valid email address"),
});

type FormData = yup.InferType<typeof schema>;

interface AddUserToDeviceDialogProps {
  visible: boolean;
  onHide: () => void;
  deviceId: number;
  serialNumber: string;
}

const AddUserToDeviceDialog: React.FC<AddUserToDeviceDialogProps> = ({
  visible,
  onHide,
  deviceId,
  serialNumber,
}) => {
  const queryClient = useQueryClient();
  const addUserMutation = useAddUserToDevice();
  const isPending = addUserMutation.isPending;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: { email: "" },
  });

  const resetForm = () => reset({ email: "" });

  const handleCancel = () => {
    resetForm();
    onHide();
  };

  const onSubmit = (data: FormData) => {
    addUserMutation.mutate(
      { deviceId, email: data.email },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["deviceUsers"] });
          notify.success(`User ${data.email} has been added to the device`);
          queryClient.invalidateQueries({
            queryKey: ["deviceUsers", serialNumber],
          });
          resetForm();
          onHide();
        },
        onError: (error: any) => {
          notify.error(
            error.response?.data?.message || "Failed to add user to device"
          );
        },
      }
    );
  };

  const footerContent = (
    <div>
      <Button
        type="button"
        label="Cancel"
        icon="pi pi-times"
        onClick={handleCancel}
        outlined
        disabled={isPending}
      />
      <Button
        type="button"
        label="Add User"
        icon="pi pi-user-plus"
        onClick={handleSubmit(onSubmit)}
        loading={isPending}
        severity="secondary"
        autoFocus
      />
    </div>
  );

  return (
    <Dialog
      header="Add User to Device"
      visible={visible}
      style={{ width: "450px" }}
      onHide={handleCancel}
      footer={footerContent}
      draggable={false}
      resizable={false}
      modal
    >
      <p>
        Enter the email address of the user you want to add to this device.
      </p>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormField>
          <FormLabel htmlFor="email">Email Address</FormLabel>
          <InputText
            placeholder="Enter email address"
            {...register("email")}
            {...getFieldAriaProps("email", errors.email?.message)}
            className={errors.email ? "p-invalid w-100" : "w-100"}
            style={{ width: "100%" }}
            disabled={isPending}
          />
          <FormError id={fieldErrorId("email")}>{errors.email?.message}</FormError>
        </FormField>
      </form>
    </Dialog>
  );
};

export default AddUserToDeviceDialog;
